import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  TextField,
  InputAdornment,
  Paper,
  List,
  ListItem,
  Typography,
  Chip,
} from '@mui/material';
import { Search as SearchIcon } from 'lucide-react';
import { getProducts } from '../firebase/products';
import { getCategories } from '../firebase/categories';
import { Product, Category } from '../types';

const SearchBar = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      const [productsData, categoriesData] = await Promise.all([
        getProducts(),
        getCategories(),
      ]);
      setProducts(productsData);
      setCategories(categoriesData);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setSearchResults(filtered);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  }, [searchTerm, products]);

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || '';
  };

  const getSubcategoryName = (subcategoryId: string | undefined) => {
    if (!subcategoryId) return '';
    const subcategory = categories.find(cat => cat.id === subcategoryId);
    return subcategory?.name || '';
  };

  const calculateDiscountedPrice = (price: number, discountPercentage?: number | null) => {
    if (!discountPercentage) return price;
    return price * (1 - discountPercentage / 100);
  };

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        size="small"
        placeholder="Buscar..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '12px',
            fontFamily: 'Rajdhani, sans-serif',
            '& fieldset': {
              borderColor: 'rgba(0, 255, 255, 0.3)',
            },
            '&:hover fieldset': {
              borderColor: '#00ffff',
              boxShadow: '0 0 10px rgba(0, 255, 255, 0.2)',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#00ffff',
              boxShadow: '0 0 15px rgba(0, 255, 255, 0.3)',
            },
          },
          '& .MuiOutlinedInput-input': {
            color: '#ffffff',
            padding: '8px 14px',
            fontFamily: 'Rajdhani, sans-serif',
            fontSize: '1rem',
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon className="text-neon-blue w-5 h-5" style={{ color: '#00ffff' }} />
            </InputAdornment>
          ),
        }}
      />

      {showResults && searchResults.length > 0 && (
        <Paper
          sx={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            left: 0,
            right: 0,
            maxHeight: { xs: '80vh', sm: '70vh' },
            minWidth: { sm: '400px', md: '500px' },
            overflow: 'auto',
            zIndex: 1000,
            backgroundColor: 'rgba(10, 10, 10, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(0, 255, 255, 0.3)',
            borderRadius: '12px',
            boxShadow: '0 4px 30px rgba(0, 255, 255, 0.15)',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(0, 0, 0, 0.5)',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#00ffff',
              borderRadius: '4px',
            },
          }}
        >
          <List sx={{ p: 0 }}>
            {searchResults.map((product) => (
              <ListItem
                key={product.id}
                component={Link}
                to={`/producto/${product.id}`}
                onClick={() => setShowResults(false)}
                sx={{
                  py: 2,
                  px: 3,
                  borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 255, 255, 0.05)',
                  },
                  textDecoration: 'none',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  {product.images.find(img => img.isPrimary) && (
                    <img
                      src={product.images.find(img => img.isPrimary)?.url}
                      alt={product.name}
                      style={{
                        width: 60,
                        height: 60,
                        objectFit: 'cover',
                        borderRadius: 8,
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    />
                  )}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{
                        color: '#ffffff',
                        fontFamily: 'Orbitron, sans-serif',
                        fontWeight: 500,
                        mb: 0.5,
                        whiteSpace: 'normal',
                        wordBreak: 'break-word',
                        textShadow: '0 0 10px rgba(0, 255, 255, 0.3)'
                      }}
                    >
                      {product.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
                      <Chip
                        label={getCategoryName(product.categoryId)}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(0, 255, 255, 0.1)',
                          color: '#00ffff',
                          border: '1px solid rgba(0, 255, 255, 0.3)',
                          height: '24px',
                          fontFamily: 'Rajdhani, sans-serif',
                          fontWeight: 600,
                        }}
                      />
                      {product.subcategoryId && (
                        <Chip
                          label={getSubcategoryName(product.subcategoryId)}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255, 0, 255, 0.1)',
                            color: '#ff00ff',
                            border: '1px solid rgba(255, 0, 255, 0.3)',
                            height: '24px',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontWeight: 600,
                          }}
                        />
                      )}
                      {product.discountPercentage && (
                        <Chip
                          label={`-${product.discountPercentage}%`}
                          size="small"
                          sx={{
                            backgroundColor: 'rgba(255, 0, 85, 0.1)',
                            color: '#ff0055',
                            border: '1px solid rgba(255, 0, 85, 0.3)',
                            height: '24px',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontWeight: 'bold',
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ 
                    textAlign: 'right',
                    minWidth: '80px',
                    ml: 2
                  }}>
                    {product.discountPercentage ? (
                      <>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#ff0055',
                            textDecoration: 'line-through',
                            display: 'block',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '0.9rem'
                          }}
                        >
                          {product.price}€
                        </Typography>
                        <Typography 
                          variant="h6" 
                          sx={{
                            color: '#00ffff',
                            fontFamily: 'Orbitron, sans-serif',
                            textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
                            fontWeight: 'bold'
                          }}
                        >
                          {calculateDiscountedPrice(product.price, product.discountPercentage).toFixed(2)}€
                        </Typography>
                      </>
                    ) : (
                      <Typography 
                        variant="h6" 
                        sx={{
                          color: '#00ffff',
                          fontFamily: 'Orbitron, sans-serif',
                          textShadow: '0 0 10px rgba(0, 255, 255, 0.5)',
                          fontWeight: 'bold'
                        }}
                      >
                        {product.price}€
                      </Typography>
                    )}
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default SearchBar;
